<template>
  <div class="new-bundle-container">
    <el-header>
      <div class="header-content">
        <h2>{{ isEditing ? 'Edit Bundle' : 'Create New Bundle' }}</h2>
        <div class="button-group">
          <el-button @click="cancel">Cancel</el-button>
          <el-button 
            type="primary" 
            @click="saveBundle"
            :loading="saving"
          >
            {{ isEditing ? 'Save' : 'Create' }}
          </el-button>
        </div>
      </div>
    </el-header>

    <el-main>
      <div class="form-container">
        <el-form 
          ref="bundleForm"
          :model="bundleForm"
          :rules="rules"
          label-position="top"
        >
          <el-tabs v-model="activeTab">
            <el-tab-pane label="Basic Info" name="basic">
              <el-form-item label="Bundle Name" prop="bundle_name">
                <el-input 
                  v-model="bundleForm.bundle_name"
                  placeholder="Enter bundle name"
                ></el-input>
              </el-form-item>

              <el-form-item label="Description" prop="bundle_description">
                <template #label>
                  <div style="display: flex; align-items: center; gap: 10px">
                    <span>Description</span>
                    <el-button
                      v-if="isSupremeUser"
                      type="primary"
                      size="small"
                      icon="el-icon-magic-stick"
                      @click="showAIDialog('Bundle Description Generation')"
                    >AI Generate</el-button>
                  </div>
                </template>
                <el-input
                  type="textarea"
                  v-model="bundleForm.bundle_description"
                  :rows="4"
                  placeholder="Enter bundle description"
                ></el-input>
              </el-form-item>

              <el-form-item label="Status" prop="bundle_status">
                <el-select v-model="bundleForm.bundle_status" style="width: 100%">
                  <el-option label="Private" value="PRIVATE"></el-option>
                  <el-option label="Published" value="PUBLISHED"></el-option>
                </el-select>
              </el-form-item>
            </el-tab-pane>

            <el-tab-pane label="Collections" name="collections">
              <div class="collections-section">
                <div class="collections-header">
                  <h3>Included Collections</h3>
                  <el-button 
                    type="primary" 
                    icon="el-icon-plus"
                    @click="showCollectionSelector"
                  >
                    Add Collection
                  </el-button>
                </div>

                <div class="collections-list">
                  <draggable 
                    v-model="bundleForm.bundle_collections"
                    @start="drag=true"
                    @end="drag=false"
                    handle=".drag-handle"
                    @change="updateCollectionOrder"
                  >
                    <el-card 
                      v-for="collection_id in bundleForm.bundle_collections" 
                      :key="collection_id"
                      class="collection-card"
                    >
                      <div class="collection-item">
                        <el-button 
                          class="drag-handle" 
                          icon="el-icon-rank"
                          type="text"
                        ></el-button>
                        <el-image 
                          :src="bundleForm.collection_array[collection_id].cover_url" 
                          fit="cover"
                          class="collection-cover"
                        >
                          <div slot="error" class="image-placeholder">
                            <i class="el-icon-picture-outline"></i>
                          </div>
                        </el-image>
                        <div class="collection-info">
                          <h4>{{ bundleForm.collection_array[collection_id].title }}</h4>
                          <p>{{ bundleForm.collection_array[collection_id].description }}</p>
                        </div>
                        <el-button 
                          type="danger" 
                          icon="el-icon-delete"
                          circle
                          @click="removeCollection(collection_id)"
                        ></el-button>
                      </div>
                    </el-card>
                  </draggable>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Pricing" name="pricing">
              <div class="pricing-container">
                <el-form-item label="Price (USD)" prop="bundle_usd_price">
                  <el-input-number 
                    v-model="bundleForm.bundle_usd_price"
                    :min="0"
                    :precision="2"
                    :step="0.01"
                    style="width: 200px"
                  ></el-input-number>
                </el-form-item>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Examination" name="examination" v-if="isEditing">
              <BundleExamConfigurationEditor
                :bundle-id="bundleForm.bundle_id"
                :collection-count="bundleForm.bundle_collections.length"
              />
            </el-tab-pane>

            <el-tab-pane label="Settings" name="settings">
              <div class="settings-container">
                <label class="settings-label">Bundle Publicity
  <el-tooltip content="You can switch the publicity of your bundle. If private, your bundle will be unlisted from our market and its details will be hidden" placement="top">
    <i class="el-icon-info" style="margin-left: 6px; color: #909399; cursor: pointer;"></i>
  </el-tooltip>
</label>
                <el-switch
                  v-model="bundleForm.bundle_status"
                  active-value="PUBLISHED"
                  inactive-value="PRIVATE"
                  active-text="Published"
                  inactive-text="Private"
                  class="status-switch"
                />
              </div>
            </el-tab-pane>

          </el-tabs>
        </el-form>
      </div>
    </el-main>

    <!-- Collection Selector Dialog -->
    <el-dialog
      title="Select Collections"
      :visible.sync="collectionSelectorVisible"
      width="70%"
    >
      <el-table
        v-loading="loadingCollections"
        :data="availableCollections"
        @selection-change="handleSelectionChange"
      >
        <el-table-column
          type="selection"
          width="55">
        </el-table-column>
        <el-table-column
          prop="title"
          label="Title">
          <template slot-scope="scope">
            {{ scope.row.title }}{{ scope.row.version_identifier ? ` (${scope.row.version_identifier})` : '' }}
          </template>
        </el-table-column>

        <el-table-column
          prop="usd_price"
          label="Price"
          width="100">
          <template slot-scope="scope">
            ${{ scope.row.usd_price }}
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="collectionSelectorVisible = false">Cancel</el-button>
        <el-button type="primary" @click="addSelectedCollections">Add Selected</el-button>
      </span>
    </el-dialog>

    <!-- AI Dialog -->
    <AIDialog
      :visible.sync="aiDialogVisible"
      :title="aiDialogTitle"
      :task-type="aiTaskType"
      :initial-input="aiDialogInitialInput"
      :context-string="aiContextString"
      @use="handleAIOutput"
    />
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import draggable from 'vuedraggable'
import AIDialog from '@/components/AIDialog.vue'
import BundleExamConfigurationEditor from '@/components/Examination/BundleExamConfigurationEditor.vue'

export default {
  name: 'NewBundle',
  components: {
    draggable,
    AIDialog,
    BundleExamConfigurationEditor
  },
  data() {
    return {
      bundleForm: {
        bundle_id: "",
        bundle_name: '',
        bundle_description: '',
        bundle_usd_price: 0,
        bundle_status: 'DRAFT',
        bundle_collections: [],
        collection_array: {}
      },
      rules: {
        bundle_name: [
          { required: true, message: 'Please enter bundle name', trigger: 'blur' }
        ],
        bundle_description: [
          { required: true, message: 'Please enter bundle description', trigger: 'blur' }
        ]
      },
      activeTab: 'basic',
      saving: false,
      collectionSelectorVisible: false,
      availableCollections: [],
      selectedCollections: [],
      loadingCollections: false,
      drag: false,
      aiDialogVisible: false,
      aiDialogTitle: '',
      aiTaskType: '',
      aiDialogInitialInput: '',
      aiContextString: '',
    }
  },
  computed: {
    isEditing() {
      return this.$route.params.id !== undefined
    },
    isSupremeUser() {
      return this.$store.state.user_info.creator_functionality_level === 'Supreme'
    }
  },
  methods: {
    async saveBundle() {
      try {
        await this.$refs.bundleForm.validate()
        
        this.saving = true
        const response = await postRequest('updateCollectionBundle', {
          ...this.bundleForm,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.$message.success('Bundle saved successfully')
          this.$router.go(-1)
        } else {
          this.$message.error(response.data.msg || 'Failed to save bundle')
        }
      } catch (error) {
        console.error('Error saving bundle:', error)
        this.$message.error('Failed to save bundle')
      } finally {
        this.saving = false
      }
    },
    cancel() {
      this.$router.go(-1)
    },
    async showCollectionSelector() {
      this.loadingCollections = true
      try {
        const response = await postRequest('getMyPostCollections', {
          user_id: this.$store.state.user_id
        })
        
        if (response.data.code === 0) {
          this.availableCollections = response.data.data.filter(collection => 
            !this.bundleForm.bundle_collections.some(
              bundleCollection_id => bundleCollection_id === (collection._id.$oid || collection._id.$id)
            )
          )
          this.collectionSelectorVisible = true
        } else {
          this.$message.error(response.data.msg || 'Failed to fetch collections')
        }
      } catch (error) {
        console.error('Error fetching collections:', error)
        this.$message.error('Failed to fetch collections')
      } finally {
        this.loadingCollections = false
      }
    },
    handleSelectionChange(val) {
      console.log('val:', val)
      this.selectedCollections = val
    },
    addSelectedCollections() {
      this.selectedCollections.forEach(collection => {
        const collectionId = (collection._id.$oid || collection._id.$id);
        this.bundleForm.bundle_collections.push(collectionId);
        this.bundleForm.collection_array[collectionId] = collection;
      });
      this.collectionSelectorVisible = false;
      this.selectedCollections = [];
    },
    removeCollection(collectionId) {
      this.bundleForm.bundle_collections = this.bundleForm.bundle_collections.filter(
        id => id !== collectionId
      );
      delete this.bundleForm.collection_array[collectionId];
    },
    async fetchBundleData() {
      if (!this.isEditing) return
      
      try {
        const response = await postRequest('getCollectionBundle', {
          bundle_id: this.$route.params.id,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          const bundle = response.data.data

          // Verify the bundle belongs to the current user
          const currentUserId = this.$store.state.user_id;
          if (bundle.user_id && bundle.user_id !== currentUserId) {
            // Bundle belongs to a different user, clear stored ID and reset
            this.$message.warning("This bundle belongs to another user. Starting a new bundle.");
            localStorage.removeItem('editBundleId');
            this.$store.dispatch('setTempBundleId', null);
            this.isEditing = false;
            return;
          }

          this.bundleForm = {
            bundle_id: (bundle._id.$oid || bundle._id.$id),
            bundle_name: bundle.bundle_name,
            bundle_description: bundle.bundle_description,
            bundle_usd_price: bundle.bundle_usd_price,
            bundle_status: bundle.bundle_status,
            bundle_collections: bundle.bundle_collections,
            collection_array: bundle.collection_array
          }
        }
      } catch (error) {
        this.$message.error('Failed to fetch bundle data')
        // Clear invalid bundle ID
        localStorage.removeItem('editBundleId');
        this.$store.dispatch('setTempBundleId', null);
        this.isEditing = false;
      }
    },
    updateCollectionOrder() {
      console.log('Collections reordered:', this.bundleForm.bundle_collections);
    },
    showAIDialog(field) {
      
      this.aiDialogTitle = field;
      this.aiTaskType = field;
      this.aiDialogInitialInput = this.bundleForm.bundle_description || '';
      
      // Prepare context for AI
      const params = {
        bundle_id: this.bundleForm.bundle_id,
        bundle_name: this.bundleForm.bundle_name,
        bundle_description: this.bundleForm.bundle_description,
        user_id: this.$store.state.user_id,
        collections: Object.values(this.bundleForm.collection_array).map(collection => ({
          title: collection.title,
          description: collection.description
        }))
      };
      this.aiContextString = JSON.stringify(params);
      this.aiDialogVisible = true;
    },
    handleAIOutput(output) {
      if (this.aiTaskType == 'Bundle Description Generation') {
        this.bundleForm.bundle_description = output;
        this.$message.success('AI content applied successfully');
      }
      this.aiDialogVisible = false;
    }
  },
  mounted() {
    this.fetchBundleData()
  }
}
</script>

<style scoped>
.new-bundle-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-container {
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.collections-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.collection-card {
  margin-bottom: 15px;
}

.collection-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collection-cover {
  width: 100px;
  height: 140px;
  border-radius: 4px;
  overflow: hidden;
}

.collection-info {
  flex: 1;
}

.collection-info h4 {
  margin: 0 0 10px 0;
}

.collection-info p {
  margin: 0;
  color: #666;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #909399;
}

.button-group {
  display: flex;
  gap: 10px;
}

.drag-handle {
  cursor: move;
  color: #909399;
  padding: 0 8px;
}

.drag-handle:hover {
  color: #409EFF;
}

.settings-container {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.settings-label {
  font-weight: 500;
  font-size: 16px;
  margin-right: 10px;
}

.status-switch {
  margin-left: 10px;
}

</style> 